import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Settings,
  Refresh,
  Visibility,
  VisibilityOff,
  Warning,
  Error as ErrorIcon,
  Info,
  CheckCircle,
  ExpandMore,
  ExpandLess,
  LocationOn,
  Schedule,
  Pets,
  Cloud,
  History,
  Group,
  Movie,
} from '@mui/icons-material';
import { apiService } from '../../services/api';

interface ProductionDailyFocusWidgetProps {
  widgetId: string;
  title: string;
  currentShootDayNumber?: number;
  onRefresh?: () => void;
  onConfigure?: () => void;
  isVisible?: boolean;
  onToggleVisibility?: () => void;
}

interface ShootDay {
  shoot_day_number: number;
  shoot_date: string;
}

interface FocusItem {
  severity: 'Critical' | 'High' | 'Medium' | 'Info';
  type: string;
  alert: string;
  details: string;
  sceneNumbers?: string[];
  data?: Record<string, any>;
}

interface FocusSummary {
  high_priority_count: number;
  elevated_count: number;
  medium_count: number;
  info_count: number;
  categories: Record<string, number>;
}

interface FocusResponse {
  items: FocusItem[];
  summary: FocusSummary | null;
  shootDate: string | null;
  shootDay: number;
}

// Category configuration for display
const CATEGORY_CONFIG: Record<string, { icon: React.ReactNode; label: string; color: string }> = {
  COMPLEXITY: { icon: <Movie fontSize="small" />, label: 'Complexity', color: '#9c27b0' },
  SCHEDULING: { icon: <Schedule fontSize="small" />, label: 'Scheduling', color: '#1976d2' },
  SPECIAL: { icon: <Pets fontSize="small" />, label: 'Special Requirements', color: '#ed6c02' },
  WEATHER: { icon: <Cloud fontSize="small" />, label: 'Weather', color: '#0288d1' },
  CARRYOVER: { icon: <History fontSize="small" />, label: "Yesterday's Issues", color: '#d32f2f' },
  INFO: { icon: <Info fontSize="small" />, label: 'Information', color: '#757575' },
};

const ProductionDailyFocusWidget: React.FC<ProductionDailyFocusWidgetProps> = ({
  widgetId,
  title,
  currentShootDayNumber = 1,
  onRefresh,
  onConfigure,
  isVisible = true,
  onToggleVisibility,
}) => {
  const [focusData, setFocusData] = useState<FocusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState(currentShootDayNumber);
  const [shootDays, setShootDays] = useState<ShootDay[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({
    COMPLEXITY: true,
    SCHEDULING: true,
    SPECIAL: true,
    WEATHER: true,
    CARRYOVER: true,
  });

  // Fetch all shoot days with dates on mount
  useEffect(() => {
    const fetchShootDays = async () => {
      try {
        const response = await apiService.getShootDays();
        if (response.success && response.data) {
          setShootDays(response.data);
        }
      } catch (err) {
        console.error('Error fetching shoot days:', err);
      }
    };
    fetchShootDays();
  }, []);

  // Fetch production daily focus data
  const fetchDailyFocus = async (day: number) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getProductionDailyFocusData(day);
      
      if (response.success && response.data) {
        // Handle both old and new API response formats
        if (Array.isArray(response.data)) {
          // Old format - convert to new format
          setFocusData({
            items: response.data.map((item: any) => ({
              severity: item.severity || 'Medium',
              type: item.type || 'Other',
              alert: item.message || item.alert || '',
              details: item.details || '',
              sceneNumbers: [],
              data: {}
            })),
            summary: null,
            shootDate: null,
            shootDay: day
          });
        } else {
          // New format
          setFocusData(response.data as FocusResponse);
        }
      } else {
        setError('Failed to retrieve production daily focus data');
      }
    } catch (err) {
      console.error('Error fetching production daily focus data:', err);
      setError('Error fetching production daily focus data');
    } finally {
      setLoading(false);
    }
  };

  // Load data when selected day changes
  useEffect(() => {
    fetchDailyFocus(selectedDay);
  }, [selectedDay]);

  // Update selected day when prop changes
  useEffect(() => {
    setSelectedDay(currentShootDayNumber);
  }, [currentShootDayNumber]);

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return <ErrorIcon sx={{ color: '#f44336' }} />;
      case 'High':
        return <Warning sx={{ color: '#ff9800' }} />;
      case 'Medium':
        return <Info sx={{ color: '#2196f3' }} />;
      case 'Info':
        return <CheckCircle sx={{ color: '#4caf50' }} />;
      default:
        return <Info sx={{ color: '#2196f3' }} />;
    }
  };

  const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'success' => {
    switch (severity) {
      case 'Critical': return 'error';
      case 'High': return 'warning';
      case 'Medium': return 'info';
      case 'Info': return 'success';
      default: return 'info';
    }
  };

  // Parse date string (YYYY-MM-DD) to Date object without timezone issues
  const parseDateString = (dateStr: string): Date => {
    if (dateStr && !dateStr.includes('T')) {
      return new Date(dateStr + 'T12:00:00');
    }
    const date = new Date(dateStr);
    date.setHours(12, 0, 0, 0);
    return date;
  };

  const getShootDate = (dayNumber: number): Date => {
    const shootDay = shootDays.find(d => d.shoot_day_number === dayNumber);
    if (shootDay?.shoot_date) {
      return parseDateString(shootDay.shoot_date);
    }
    if (focusData?.shootDate && dayNumber === selectedDay) {
      return parseDateString(focusData.shootDate);
    }
    const baseDate = new Date('2026-01-18T12:00:00');
    const fallbackDate = new Date(baseDate);
    fallbackDate.setDate(baseDate.getDate() + (dayNumber - 1));
    return fallbackDate;
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Group items by category
  const groupedItems = React.useMemo(() => {
    if (!focusData?.items) return {};
    return focusData.items.reduce((acc, item) => {
      const category = item.type || 'INFO';
      if (!acc[category]) acc[category] = [];
      acc[category].push(item);
      return acc;
    }, {} as Record<string, FocusItem[]>);
  }, [focusData]);

  // Count high priority items
  const highPriorityCount = focusData?.items?.filter(
    item => item.severity === 'Critical' || item.severity === 'High'
  ).length || 0;

  if (!isVisible) {
    return null;
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#1a1a2e' }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              {highPriorityCount > 0 && (
                <Chip
                  label={`${highPriorityCount} Priority`}
                  color="error"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Shoot Day</InputLabel>
                <Select
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(Number(e.target.value))}
                  label="Shoot Day"
                  sx={{ color: 'white' }}
                >
                  {(shootDays.length > 0 ? shootDays : Array.from({ length: 26 }, (_, i) => ({ shoot_day_number: i + 1, shoot_date: '' }))).map((day) => (
                    <MenuItem key={day.shoot_day_number} value={day.shoot_day_number}>
                      Day {day.shoot_day_number} - {formatDate(getShootDate(day.shoot_day_number))}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Tooltip title="Refresh">
                <IconButton onClick={() => fetchDailyFocus(selectedDay)} size="small">
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Configure">
                <IconButton onClick={onConfigure} size="small">
                  <Settings />
                </IconButton>
              </Tooltip>
              <Tooltip title={isVisible ? "Hide" : "Show"}>
                <IconButton onClick={onToggleVisibility} size="small">
                  {isVisible ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Key Production Risks & Priorities for {formatDate(getShootDate(selectedDay))}
          </Typography>
        }
      />
      <CardContent sx={{ flex: 1, overflow: 'auto', pt: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : !focusData?.items || focusData.items.length === 0 ? (
          <Box 
            textAlign="center" 
            py={4}
            sx={{
              background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
              borderRadius: 2,
              border: '1px solid rgba(76, 175, 80, 0.3)'
            }}
          >
            <CheckCircle sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
            <Typography variant="h6" color="success.main" gutterBottom>
              All Clear
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No major production risks identified for this day
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* Summary Bar */}
            {focusData.summary && (
              <Box 
                sx={{ 
                  display: 'flex', 
                  gap: 1, 
                  mb: 2, 
                  p: 1.5,
                  bgcolor: 'rgba(255,255,255,0.03)',
                  borderRadius: 1
                }}
              >
                {focusData.summary.high_priority_count > 0 && (
                  <Chip 
                    icon={<ErrorIcon />}
                    label={`${focusData.summary.high_priority_count} Critical`} 
                    color="error" 
                    size="small" 
                  />
                )}
                {focusData.summary.elevated_count > 0 && (
                  <Chip 
                    icon={<Warning />}
                    label={`${focusData.summary.elevated_count} High`} 
                    color="warning" 
                    size="small" 
                  />
                )}
                {focusData.summary.medium_count > 0 && (
                  <Chip 
                    icon={<Info />}
                    label={`${focusData.summary.medium_count} Medium`} 
                    color="info" 
                    size="small" 
                  />
                )}
              </Box>
            )}

            {/* Categorized Risk Items */}
            {Object.entries(groupedItems).map(([category, items]) => {
              const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.INFO;
              const isExpanded = expandedCategories[category] ?? true;
              const hasCritical = items.some(i => i.severity === 'Critical');
              
              return (
                <Box key={category} sx={{ mb: 2 }}>
                  {/* Category Header */}
                  <Box
                    onClick={() => toggleCategory(category)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1,
                      bgcolor: hasCritical ? 'rgba(244, 67, 54, 0.15)' : 'rgba(255,255,255,0.05)',
                      borderRadius: 1,
                      cursor: 'pointer',
                      borderLeft: `3px solid ${config.color}`,
                      '&:hover': {
                        bgcolor: hasCritical ? 'rgba(244, 67, 54, 0.2)' : 'rgba(255,255,255,0.08)',
                      }
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {config.icon}
                      <Typography variant="subtitle2" fontWeight={600}>
                        {config.label}
                      </Typography>
                      <Chip 
                        label={items.length} 
                        size="small" 
                        sx={{ 
                          height: 20, 
                          fontSize: '0.75rem',
                          bgcolor: hasCritical ? 'error.main' : 'rgba(255,255,255,0.1)'
                        }} 
                      />
                    </Box>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>
                  
                  {/* Category Items */}
                  <Collapse in={isExpanded}>
                    <Box sx={{ pl: 2, pt: 1 }}>
                      {items.map((item, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 1.5,
                            p: 1.5,
                            mb: 1,
                            bgcolor: 'rgba(255,255,255,0.02)',
                            borderRadius: 1,
                            borderLeft: `2px solid ${
                              item.severity === 'Critical' ? '#f44336' :
                              item.severity === 'High' ? '#ff9800' :
                              item.severity === 'Medium' ? '#2196f3' : '#4caf50'
                            }`
                          }}
                        >
                          <Box sx={{ mt: 0.5 }}>
                            {getSeverityIcon(item.severity)}
                          </Box>
                          <Box flex={1}>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {item.alert}
                              </Typography>
                              <Chip
                                label={item.severity}
                                color={getSeverityColor(item.severity)}
                                size="small"
                                sx={{ height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                            <Typography variant="body2" color="text.secondary">
                              {item.details}
                            </Typography>
                            {item.sceneNumbers && item.sceneNumbers.length > 0 && (
                              <Box display="flex" gap={0.5} mt={1} flexWrap="wrap">
                                {item.sceneNumbers.map((scene, sIdx) => (
                                  <Chip 
                                    key={sIdx}
                                    label={`Sc. ${scene}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ProductionDailyFocusWidget;
